import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectGroup, SelectLabel, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { User, MapPin, Search, AlertTriangle, PackageOpen, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/**
 * HandInPSADialog — predare PSA către magazie / altă locație / altă persoană.
 * Pasul 1: alegi sursa (persoană sau locație)
 * Pasul 2: vezi exact PSA disponibilă la acea sursă
 * Pasul 3: bifezi ce predai + alegi destinația (locație SAU persoană) + status + notă
 * Pasul 4: submit → fiecare item rulează /inventory/{iid}/move
 *
 * Props:
 *  - items: lista globală PSA (sau toate item-urile filtrate doar PSA)
 *  - meta: meta info inventory (pentru locații)
 *  - onClose, onDone
 */
export default function HandInPSADialog({ items, meta, onClose, onDone }) {
  const [sourceType, setSourceType] = useState("person"); // 'person' | 'location'
  const [sourceId, setSourceId] = useState(""); // person_id sau location label
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Destinație combinată: valoare cu prefix `loc:<name>` sau `person:<id>`
  const [destValue, setDestValue] = useState("loc:MAGAZIE");
  const [destStatus, setDestStatus] = useState("functional");
  const [note, setNote] = useState("");
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [persons, setPersons] = useState([]);
  const todayIso = new Date().toISOString().slice(0, 10);
  const [operationDate, setOperationDate] = useState(todayIso);

  const psaItems = useMemo(() => (items || []).filter((it) => (it.item_type || "") === "psa"), [items]);

  // Încarcă persoanele active (pentru destinație)
  useEffect(() => {
    api.get("/persons").then((r) => {
      const list = (r.data || []).filter((p) => p.active !== false);
      list.sort((a, b) => {
        const na = `${a.nume || ""} ${a.prenume || ""}`.trim().toLowerCase();
        const nb = `${b.nume || ""} ${b.prenume || ""}`.trim().toLowerCase();
        return na.localeCompare(nb);
      });
      setPersons(list);
    }).catch(() => setPersons([]));
  }, []);

  // Surse disponibile: persoane care au PSA + locații care au PSA
  const personOptions = useMemo(() => {
    const map = new Map();
    psaItems.forEach((it) => {
      if (it.assignee_person_id && it.assignee) {
        if (!map.has(it.assignee_person_id)) {
          map.set(it.assignee_person_id, { id: it.assignee_person_id, name: it.assignee, count: 0 });
        }
        map.get(it.assignee_person_id).count += 1;
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, "ro"));
  }, [psaItems]);

  const locationOptions = useMemo(() => {
    const map = new Map();
    psaItems.forEach((it) => {
      if (!it.assignee_person_id && it.location) {
        if (!map.has(it.location)) {
          map.set(it.location, { id: it.location, name: it.location, count: 0 });
        }
        map.get(it.location).count += 1;
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, "ro"));
  }, [psaItems]);

  // Reset selectedIds la schimbarea sursei
  useEffect(() => {
    setSelectedIds(new Set());
  }, [sourceType, sourceId]);

  // Items la sursa aleasă
  const sourceItems = useMemo(() => {
    if (!sourceId) return [];
    const q = search.trim().toLowerCase();
    let list;
    if (sourceType === "person") {
      list = psaItems.filter((it) => it.assignee_person_id === sourceId);
    } else {
      list = psaItems.filter((it) => !it.assignee_person_id && it.location === sourceId);
    }
    if (q) {
      list = list.filter((it) => `${it.name || ""} ${it.serial || ""} ${it.psa_category || ""} ${it.size || ""}`.toLowerCase().includes(q));
    }
    return list.sort((a, b) => (a.name || "").localeCompare(b.name || "", "ro"));
  }, [psaItems, sourceType, sourceId, search]);

  const toggle = (id) => {
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const selectAll = () => setSelectedIds(new Set(sourceItems.map((it) => it.id)));
  const deselectAll = () => setSelectedIds(new Set());

  // Listă combinată de locații (pentru destinație) — include MAGAZIE, locațiile din meta și cele întâlnite în PSA
  const locationLabels = useMemo(() => {
    const set = new Set(["MAGAZIE"]);
    (meta?.locations || []).forEach((l) => l?.name && set.add(l.name));
    psaItems.forEach((it) => { if (it.location) set.add(it.location); });
    return Array.from(set).sort();
  }, [meta, psaItems]);

  // Persoane disponibile ca destinație — toate persoanele active, mai puțin sursa dacă e persoană
  const destPersons = useMemo(() => {
    return persons.filter((p) => !(sourceType === "person" && p.id === sourceId));
  }, [persons, sourceType, sourceId]);

  // Helpers destinație
  const destInfo = useMemo(() => {
    if (!destValue) return { kind: null, value: null };
    if (destValue.startsWith("person:")) {
      const pid = destValue.slice(7);
      const p = persons.find((x) => x.id === pid);
      const label = p ? `${p.nume || ""} ${p.prenume || ""}`.trim() : "(persoană)";
      return { kind: "person", id: pid, label };
    }
    if (destValue.startsWith("loc:")) {
      return { kind: "location", value: destValue.slice(4) };
    }
    // fallback (text liber introdus)
    return { kind: "location", value: destValue };
  }, [destValue, persons]);

  const submit = async () => {
    if (selectedIds.size === 0) {
      toast.error("Selectează cel puțin un item de predat");
      return;
    }
    if (!operationDate) {
      toast.error("Specifică data predării");
      return;
    }
    if (destInfo.kind === "location" && !((destInfo.value || "").trim())) {
      toast.error("Specifică locația destinație");
      return;
    }
    if (destInfo.kind === "person" && !destInfo.id) {
      toast.error("Selectează persoana destinație");
      return;
    }
    setSubmitting(true);
    let ok = 0, fail = 0;
    const sourceLabel = sourceType === "person"
      ? (personOptions.find((p) => p.id === sourceId)?.name || "persoană")
      : (sourceId || "locație");
    const baseNote = note.trim()
      ? `Predat de ${sourceLabel}: ${note.trim()}`
      : `Predat de ${sourceLabel}`;
    const fullNote = destInfo.kind === "person"
      ? `${baseNote} → către ${destInfo.label}`
      : baseNote;

    for (const iid of selectedIds) {
      const it = sourceItems.find((x) => x.id === iid);
      try {
        const payload = {
          color: null,
          shelf_col: null,
          shelf_row: null,
          new_status: destStatus,
          recipient_note: fullNote,
          operation_date: operationDate || null,
        };
        if (destInfo.kind === "person") {
          // Transfer către o persoană → asignează acelei persoane.
          // Păstrăm locația curentă a item-ului ca depozitare fizică implicită.
          payload.location = it?.location || "MAGAZIE";
          payload.assignee_person_id = destInfo.id;
          payload.assignee_clear = false;
        } else {
          // Transfer către o locație → eliberează asignatul (revine în stoc la acea locație).
          payload.location = (destInfo.value || "").trim();
          payload.assignee_clear = true;
        }
        await api.post(`/inventory/${iid}/move`, payload);
        ok += 1;
      } catch (_e) {
        fail += 1;
      }
    }
    setSubmitting(false);
    const destLabel = destInfo.kind === "person" ? destInfo.label : destInfo.value;
    if (fail === 0) {
      toast.success(`✓ ${ok} ${ok === 1 ? "PSA predat" : "PSA predate"} către ${destLabel}`);
      onDone && onDone();
    } else {
      toast.warning(`Predat: ${ok} · Eroare: ${fail}`);
      onDone && onDone();
    }
  };

  const canSubmit = selectedIds.size > 0 && operationDate && (
    (destInfo.kind === "location" && (destInfo.value || "").trim()) ||
    (destInfo.kind === "person" && destInfo.id)
  );

  return (
    <Dialog open onOpenChange={(v) => !v && !submitting && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="handin-psa-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <PackageOpen size={20}/>
            📥 Predă PSA către magazie sau o persoană
          </DialogTitle>
          <DialogDescription className="text-xs">
            Alege sursa (persoană sau locație), bifează exact ce echipamente sunt predate și unde merg — într-o locație nouă sau direct la altă persoană.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 overflow-y-auto pr-1 flex-1">
          {/* PASUL 1: Sursa */}
          <div className="card-flat p-3 bg-amber-50/40 border-l-4 border-amber-400">
            <div className="text-[11px] uppercase tracking-wider font-bold text-amber-800 mb-2">Pasul 1 · de unde se predă?</div>
            <Tabs value={sourceType} onValueChange={(v) => { setSourceType(v); setSourceId(""); }}>
              <TabsList className="grid w-full grid-cols-2 h-9">
                <TabsTrigger value="person" data-testid="handin-tab-person"><User size={14} className="mr-1.5"/>De la o persoană</TabsTrigger>
                <TabsTrigger value="location" data-testid="handin-tab-location"><MapPin size={14} className="mr-1.5"/>De la o locație</TabsTrigger>
              </TabsList>
              <TabsContent value="person" className="mt-2">
                <Select value={sourceId} onValueChange={setSourceId}>
                  <SelectTrigger className="h-9" data-testid="handin-source-person"><SelectValue placeholder="Selectează persoana..."/></SelectTrigger>
                  <SelectContent>
                    {personOptions.length === 0 ? (
                      <div className="px-2 py-1.5 text-xs text-zinc-500 italic">Nicio persoană nu are PSA atribuit.</div>
                    ) : personOptions.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name} ({p.count} PSA)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TabsContent>
              <TabsContent value="location" className="mt-2">
                <Select value={sourceId} onValueChange={setSourceId}>
                  <SelectTrigger className="h-9" data-testid="handin-source-location"><SelectValue placeholder="Selectează locația..."/></SelectTrigger>
                  <SelectContent>
                    {locationOptions.length === 0 ? (
                      <div className="px-2 py-1.5 text-xs text-zinc-500 italic">Nicio locație cu PSA în stoc.</div>
                    ) : locationOptions.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.name} ({l.count} PSA)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TabsContent>
            </Tabs>
          </div>

          {/* PASUL 2: Items la sursă */}
          {sourceId && (
            <div className="card-flat p-3 border-l-4 border-blue-400">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <div className="text-[11px] uppercase tracking-wider font-bold text-blue-800">
                  Pasul 2 · ce se predă? ({selectedIds.size}/{sourceItems.length} bifate)
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={selectAll} data-testid="handin-select-all">Bifează tot</Button>
                  <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={deselectAll} data-testid="handin-deselect-all">Debifează</Button>
                </div>
              </div>
              <div className="relative mb-2">
                <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400"/>
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Caută PSA (nume, serie, categorie, mărime)..." className="pl-7 h-8 text-xs" data-testid="handin-search"/>
              </div>
              {sourceItems.length === 0 ? (
                <div className="py-6 text-center text-xs text-zinc-500 italic">
                  Nicio piesă PSA disponibilă la această sursă.
                </div>
              ) : (
                <div className="max-h-[260px] overflow-y-auto border rounded divide-y" data-testid="handin-items-list">
                  {sourceItems.map((it) => {
                    const sel = selectedIds.has(it.id);
                    return (
                      <label
                        key={it.id}
                        className={`flex items-center gap-2 p-2 cursor-pointer transition-colors ${sel ? "bg-emerald-50" : "hover:bg-zinc-50"}`}
                        data-testid={`handin-item-${it.id}`}
                      >
                        <Checkbox checked={sel} onCheckedChange={() => toggle(it.id)} data-testid={`handin-check-${it.id}`}/>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                            <span className="truncate">{it.name}</span>
                            {it.psa_category && <span className="text-[9px] font-bold uppercase bg-cyan-100 text-cyan-800 px-1 py-0.5 rounded">{it.psa_category}</span>}
                            {it.size && <span className="text-[9px] font-bold uppercase bg-blue-100 text-blue-800 px-1 py-0.5 rounded">{it.size}</span>}
                            {it.status === "defect" && <span className="text-[9px] font-bold uppercase bg-red-100 text-red-800 px-1 py-0.5 rounded">DEFECT</span>}
                          </div>
                          <div className="text-[10px] text-zinc-500 mt-0.5 flex gap-2 flex-wrap">
                            {it.serial && <span className="font-mono">SN: {it.serial}</span>}
                            <span>Cantitate: <strong>{it.quantity ?? 1}</strong></span>
                            {it.location && <span>📍 {it.location}</span>}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* PASUL 3: Destinație */}
          {sourceId && selectedIds.size > 0 && (
            <div className="card-flat p-3 border-l-4 border-emerald-400 bg-emerald-50/40">
              <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-800 mb-2 flex items-center gap-1">
                Pasul 3 · unde merg echipamentele? <ArrowRight size={12} className="opacity-60"/>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Label className="small-label">Destinație *</Label>
                  <Select value={destValue} onValueChange={setDestValue}>
                    <SelectTrigger className="h-9 mt-1" data-testid="handin-dest-combined">
                      <SelectValue placeholder="Alege o locație sau o persoană..."/>
                    </SelectTrigger>
                    <SelectContent className="max-h-[320px]">
                      <SelectGroup>
                        <SelectLabel className="text-[10px] uppercase tracking-wider text-emerald-700">
                          📦 Locații
                        </SelectLabel>
                        {locationLabels.map((l) => (
                          <SelectItem key={`loc-${l}`} value={`loc:${l}`} data-testid={`handin-dest-loc-${l}`}>
                            <span className="inline-flex items-center gap-1.5">
                              <MapPin size={12} className="text-emerald-600"/>{l}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                      {destPersons.length > 0 && (
                        <SelectGroup>
                          <SelectLabel className="text-[10px] uppercase tracking-wider text-cyan-700 mt-1">
                            👤 Persoane
                          </SelectLabel>
                          {destPersons.map((p) => {
                            const label = `${p.nume || ""} ${p.prenume || ""}`.trim() || "(fără nume)";
                            return (
                              <SelectItem key={`person-${p.id}`} value={`person:${p.id}`} data-testid={`handin-dest-person-${p.id}`}>
                                <span className="inline-flex items-center gap-1.5">
                                  <User size={12} className="text-cyan-600"/>{label}
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                  {destInfo.kind === "person" && (
                    <div className="text-[10px] text-cyan-700 mt-1 flex items-center gap-1">
                      <User size={10}/> Articolele vor fi asignate direct lui <strong>{destInfo.label}</strong>.
                    </div>
                  )}
                  {destInfo.kind === "location" && (
                    <div className="text-[10px] text-emerald-700 mt-1 flex items-center gap-1">
                      <MapPin size={10}/> Articolele vor reveni în stoc la <strong>{destInfo.value}</strong>.
                    </div>
                  )}
                </div>
                <div>
                  <Label className="small-label">Stare la predare</Label>
                  <Select value={destStatus} onValueChange={setDestStatus}>
                    <SelectTrigger className="h-9 mt-1" data-testid="handin-dest-status"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="functional">✓ Funcțional</SelectItem>
                      <SelectItem value="defect">⚠ Defect</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="small-label flex items-center gap-1">📅 Data predării <span className="text-red-600">*</span></Label>
                  <Input
                    type="date"
                    value={operationDate}
                    onChange={(e) => setOperationDate(e.target.value)}
                    max={todayIso}
                    required
                    className="mt-1 text-sm h-9"
                    data-testid="handin-operation-date"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="small-label">Notă (opțional)</Label>
                  <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="ex: predat după proiectul Saplun, urme de uzură normală" className="mt-1 text-sm" data-testid="handin-note"/>
                </div>
              </div>
              {destStatus === "defect" && (
                <div className="mt-2 text-[11px] text-red-700 flex items-center gap-1 font-semibold">
                  <AlertTriangle size={12}/> Item-urile vor fi marcate ca DEFECT — admin va decide dacă merită reparate sau casate.
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-3 mt-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Anulează</Button>
          <Button
            onClick={submit}
            disabled={submitting || !canSubmit}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="handin-submit"
          >
            <PackageOpen size={14} className="mr-1.5"/>
            {submitting ? "Se procesează..." : `Predă ${selectedIds.size} ${selectedIds.size === 1 ? "PSA" : "PSA"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
